"use client";
import { History, Refresh } from "@mui/icons-material";
import {
	Box,
	Button,
	Card,
	CardContent,
	CardHeader,
	Collapse,
	IconButton,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Typography,
} from "@mui/material";
import type React from "react";
import { useCallback, useEffect, useRef, useState } from "react";
import useSWR from "swr";
import { SWR_KEYS } from "@/config/constants";
import { useAuth } from "@/features/auth";
import { useS3Operations } from "@/shared/hooks";
import type { TermType, TermVersion } from "@/shared/types";

interface VersionHistorySectionProps {
	type: TermType;
	onContentFetch: (version: string, htmlContent: string) => void;
	onError?: (message: string) => void;
}

export default function VersionHistorySection({
	type,
	onContentFetch,
	onError,
}: VersionHistorySectionProps): React.ReactElement {
	const { isAuthenticated } = useAuth();
	const [showHistory, setShowHistory] = useState(false);
	const [loadingVersion, setLoadingVersion] = useState<string | null>(null);
	const onErrorRef = useRef(onError);
	const { getTermVersionHistory, getTermHtml } = useS3Operations();

	// SWRでバージョン履歴を取得
	const {
		data: versionsHistory = [],
		isLoading: loadingHistory,
		mutate,
		error,
	} = useSWR<TermVersion[]>(
		isAuthenticated ? SWR_KEYS.termVersionHistory(type) : null,
		() => getTermVersionHistory(type),
		{
			revalidateOnFocus: false,
		},
	);

	// onErrorの最新の値をrefに保存
	useEffect(() => {
		onErrorRef.current = onError;
	}, [onError]);

	// エラー発生時にコールバック
	useEffect(() => {
		if (error && onErrorRef.current) {
			const errorMessage = `バージョン履歴の取得に失敗しました: ${error instanceof Error ? error.message : String(error)}`;
			onErrorRef.current(errorMessage);
		}
	}, [error]);

	const handleFetchContent = useCallback(
		async (termVersion: TermVersion) => {
			setLoadingVersion(termVersion.version);
			try {
				const htmlContent = await getTermHtml(termVersion.file);
				onContentFetch(termVersion.version, htmlContent);
			} catch (error) {
				const errorMessage = `利用規約の取得に失敗しました: ${error instanceof Error ? error.message : String(error)}`;
				if (onErrorRef.current) {
					onErrorRef.current(errorMessage);
				}
			} finally {
				setLoadingVersion(null);
			}
		},
		[getTermHtml, onContentFetch],
	);

	return (
		<Card sx={{ mb: 2 }}>
			<CardHeader
				title={
					<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
						<History />
						<Typography variant="h6">利用規約バージョン履歴</Typography>
					</Box>
				}
				action={
					<Box sx={{ display: "flex", gap: 1 }}>
						<IconButton onClick={() => mutate()} disabled={loadingHistory} size="small">
							<Refresh />
						</IconButton>
						<Button variant="outlined" size="small" onClick={() => setShowHistory(!showHistory)}>
							{showHistory ? "閉じる" : "表示"}
						</Button>
					</Box>
				}
				sx={{ pb: 1 }}
			/>
			<Collapse in={showHistory}>
				<CardContent sx={{ pt: 0 }}>
					{loadingHistory ? (
						<Box sx={{ textAlign: "center", py: 2 }}>
							<Typography>読み込み中...</Typography>
						</Box>
					) : versionsHistory.length === 0 ? (
						<Box sx={{ textAlign: "center", py: 2 }}>
							<Typography color="text.secondary">バージョン履歴がありません</Typography>
						</Box>
					) : (
						<TableContainer>
							<Table size="small">
								<TableHead>
									<TableRow>
										<TableCell>バージョン</TableCell>
										<TableCell>アップロード日時</TableCell>
										<TableCell />
									</TableRow>
								</TableHead>
								<TableBody>
									{versionsHistory.map((termVersion) => (
										<TableRow key={termVersion.version}>
											<TableCell>{termVersion.version}</TableCell>
											<TableCell>{new Date(termVersion.date).toLocaleString("ja-JP")}</TableCell>
											<TableCell>
												<Button
													variant="outlined"
													size="small"
													color="secondary"
													disabled={loadingVersion === termVersion.version}
													onClick={() => handleFetchContent(termVersion)}
												>
													{loadingVersion === termVersion.version ? "読み込み中..." : "規約を表示"}
												</Button>
											</TableCell>
										</TableRow>
									))}
								</TableBody>
							</Table>
						</TableContainer>
					)}
				</CardContent>
			</Collapse>
		</Card>
	);
}
